// FormBuilderValidationSection — the "Data validation" body inside Advanced
// options, per the design: each boolean renders as an icon toggle-card
// (icon tile · title · description · Switch); toggles with dependents reveal
// an indented sub-row beneath (Min/Max pair under "Limit characters", date
// bounds for dateTime). Geofence is special-cased per design: ⓘ tooltip
// instead of a description, and its dependents render as one cluster —
// range slider + Meters readout, radius preview, two checkboxes. Rows are
// driven by the selected type's validationProperties, ordered by ROW_ORDER.

import { useTranslation } from 'react-i18next';
import { Checkbox, Icon, Input, Slider, Switch } from '@/components/common';
import { formElementDefaultProperties, type ElementProperties } from './utils/formElementDefaults';
import type { BuilderField, BuilderPage, GeofenceGate } from './utils/builderModel';

/** Design order of the top-level toggle cards. Geofence leads — only
 *  `location` declares it, so no other type's ordering shifts. */
const ROW_ORDER = [
  'geofence',
  'validateRequiredFields',
  'required',
  'readOnly',
  'hideOnPdf',
  'hidden',
  'enableScoring',
  'restriction',
  'showStamp',
  'allowSelectLocation',
  'allowShareLocation',
  'sendAfterSubmit',
  'sendAfterApproval',
  'restrictToSubmit',
  'showDataStatus',
];

/** Rows whose `_desc` renders as an ⓘ tooltip beside the title instead of a
 *  description line (per design — geofence only, so far). */
const INFO_ROWS = new Set(['geofence']);

/** Geofence range slider bounds (design: 5–500 meters). */
const GEO_MIN = 5;
const GEO_MAX = 500;

/** Radius-preview diameter (px) for a range value — linear between the design
 *  endpoints; an illustration, not a to-scale map. */
const geoPreviewSize = (range: number) =>
  Math.round(
    56 + ((Math.min(GEO_MAX, Math.max(GEO_MIN, range)) - GEO_MIN) / (GEO_MAX - GEO_MIN)) * 84,
  );

/** Icon tile per toggle card. */
const ROW_ICON: Record<string, string> = {
  validateRequiredFields: 'clipboard-check',
  required: 'asterisk',
  readOnly: 'lock',
  hideOnPdf: 'file-x',
  hidden: 'eye-off',
  restriction: 'ruler',
  enableScoring: 'calculator',
  showStamp: 'stamp',
  allowSelectLocation: 'map-pin',
  allowShareLocation: 'share-2',
  geofence: 'radar',
  sendAfterSubmit: 'send',
  sendAfterApproval: 'circle-check',
  restrictToSubmit: 'shield-check',
  showDataStatus: 'info',
};

/** Dependent keys revealed while their parent toggle is on. */
const DEPENDENTS: Record<string, string[]> = {
  restriction: ['minRange', 'maxRange', 'minDate', 'maxDate'],
  geofence: ['geofenceRange', 'geofenceRestricted', 'geofenceEnableDirection'],
};

/** Toggles that force another one off and disabled while they are on.
 *  `required` <-> `hidden` is a deliberate MUTUAL pair: whichever is on locks the
 *  other, so the UI can never produce both (only already-saved data can). */
const BLOCKED_BY: Record<string, string[]> = {
  required: ['hidden'],
  readOnly: ['required', 'hidden'],
  restrictToSubmit: ['hidden'],
  hideOnPdf: ['required', 'hidden'],
  hidden: ['required'],
};

/** The inverse of BLOCKED_BY — blocker → the keys it clears — derived so the
 *  rules live in ONE place. Two hand-written tables would drift, and the disable
 *  check and the clearing patch have to agree exactly. */
const CLEARS: Record<string, string[]> = Object.entries(BLOCKED_BY).reduce<
  Record<string, string[]>
>((acc, [key, blockers]) => {
  for (const blocker of blockers) (acc[blocker] ??= []).push(key);
  return acc;
}, {});

const DATE_KEYS = new Set(['minDate', 'maxDate']);

interface FormBuilderValidationSectionProps {
  field: BuilderField | BuilderPage;
  onChange: (properties: ElementProperties) => void;
  /** Whole-form geofence gate, present only for location fields. */
  geofenceGate?: GeofenceGate;
}

const FormBuilderValidationSection = ({
  field,
  onChange,
  geofenceGate,
}: FormBuilderValidationSectionProps) => {
  const { t } = useTranslation();

  const properties = field.properties;
  const defaults = formElementDefaultProperties[field.elementType];
  if (!('validationProperties' in properties) || !('validationProperties' in defaults)) {
    return null;
  }
  const vp = properties.validationProperties as Record<string, unknown>;
  const typeKeys = new Set(Object.keys(defaults.validationProperties));

  const setValidation = (patch: Record<string, unknown>) =>
    onChange({
      ...properties,
      validationProperties: { ...(properties.validationProperties as object), ...patch },
    } as ElementProperties);

  // "Limit characters" reads differently per type (length / value / dates).
  const rowLabelKey = (key: string) => {
    if (key === 'restriction' && field.elementType === 'number') {
      return 'forms_builder_props_val_restriction_number';
    }
    if (key === 'restriction' && field.elementType === 'dateTime') {
      return 'forms_builder_props_val_restriction_date';
    }
    return `forms_builder_props_val_${key}`;
  };

  const rows = ROW_ORDER.filter((key) => typeKeys.has(key));

  // Blockers currently on — a blocker only counts if THIS type carries it
  // (restrictToSubmit is otp-only, readOnly is on 8 of 16 types).
  const blockersOn = (key: string) =>
    (BLOCKED_BY[key] ?? []).filter((b) => typeKeys.has(b) && vp[b] === true);

  // A toggle that is ON is never disabled: turning something off must always be
  // possible. That invariant is also what frees an element saved with `required`
  // and `hidden` both on — both read as on, so neither locks the other, and
  // unchecking either restores normal behaviour.
  const disabledBy = (key: string) => (vp[key] === true ? [] : blockersOn(key));

  const toggleRow = (key: string, on: boolean) => {
    if (!on) {
      setValidation({ [key]: false });
      return;
    }
    // Turning a blocker on clears everything it blocks, in ONE patch — one
    // onChange, so one dirty mark. Nothing is remembered: unchecking the blocker
    // later leaves these off.
    const patch: Record<string, unknown> = { [key]: true };
    for (const blocked of CLEARS[key] ?? []) if (typeKeys.has(blocked)) patch[blocked] = false;
    setValidation(patch);
  };

  // Geofence dependents render as one designed cluster (slider + readout,
  // radius preview, checkboxes) instead of the generic sub-rows.
  const geofenceCluster = () => {
    const range = Number(vp.geofenceRange ?? GEO_MIN);
    const size = geoPreviewSize(range);
    return (
      <div className='form-builder-props__geo'>
        <div className='form-builder-props__geo-range'>
          <div className='form-builder-props__geo-track'>
            <Slider
              min={GEO_MIN}
              max={GEO_MAX}
              step={1}
              value={range}
              aria-label={t('forms_builder_props_val_geofenceRange')}
              onChange={(e) => setValidation({ geofenceRange: Number(e.target.value) })}
            />
            {/* numeric bounds are data, not copy — rendered literally */}
            <span className='form-builder-props__geo-ends' aria-hidden='true'>
              <span>{GEO_MIN}</span>
              <span>{GEO_MAX}</span>
            </span>
          </div>
          <span className='form-builder-props__geo-readout'>
            <strong>{range}</strong> {t('forms_builder_props_val_geofenceRange_unit')}
          </span>
        </div>

        <span
          className='form-builder-props__geo-preview'
          style={{ width: size, height: size }}
          aria-hidden='true'
        >
          <Icon name='map-pin' size={20} />
        </span>

        <Checkbox
          label={t('forms_builder_props_val_geofenceRestricted')}
          checked={vp.geofenceRestricted === true}
          onChange={(e) => setValidation({ geofenceRestricted: e.target.checked })}
          size='sm'
        />
        <Checkbox
          label={t('forms_builder_props_val_geofenceEnableDirection')}
          checked={vp.geofenceEnableDirection === true}
          onChange={(e) => setValidation({ geofenceEnableDirection: e.target.checked })}
          size='sm'
        />
      </div>
    );
  };

  // One dependent control: numeric/date input pair members or nested switches.
  const subControl = (key: string) => {
    const label = t(`forms_builder_props_val_${key}`);
    const value = vp[key];
    if (typeof value === 'boolean') {
      return (
        <div key={key} className='form-builder-props__val-sub-row'>
          <span className='form-builder-props__val-sub-label'>{label}</span>
          <Switch
            checked={value}
            aria-label={label}
            onChange={(e) => setValidation({ [key]: e.target.checked })}
          />
        </div>
      );
    }
    return (
      <div key={key} className='form-builder-props__val-sub-pair'>
        <span className='form-builder-props__val-sub-label'>{label}</span>
        <Input
          size='sm'
          type={DATE_KEYS.has(key) ? 'date' : 'number'}
          className={[
            'form-builder-props__val-sub-input',
            DATE_KEYS.has(key) && 'form-builder-props__val-sub-input--date',
          ]
            .filter(Boolean)
            .join(' ')}
          value={String(value ?? '')}
          aria-label={label}
          onChange={(e) =>
            setValidation({
              [key]: DATE_KEYS.has(key) ? e.target.value : Number(e.target.value),
            })
          }
        />
      </div>
    );
  };

  return (
    <div className='form-builder-props__val'>
      {rows.map((key) => {
        const labelKey = rowLabelKey(key);
        const dependents = (DEPENDENTS[key] ?? []).filter((d) => typeKeys.has(d));
        const blocked = disabledBy(key);
        // Geofence is gated across the FORM (geo-column binding + one per
        // form), not by same-field blockers — same disabled-Switch idiom,
        // different source of truth. The ON-is-never-disabled invariant above
        // applies here too, so stale saved state can always be turned off.
        // takenByOther implies bound, so the two reasons cannot collide.
        const geofenceBlockReason =
          key === 'geofence' && vp[key] !== true && geofenceGate
            ? geofenceGate.takenByOther
              ? t('forms_builder_props_val_geofence_taken')
              : !geofenceGate.bound
                ? t('forms_builder_props_val_geofence_desc')
                : null
            : null;
        const rowDisabled = blocked.length > 0 || geofenceBlockReason !== null;
        return (
          <div key={key} className='form-builder-props__val-item'>
            <div
              className={[
                'form-builder-props__val-card',
                rowDisabled && 'form-builder-props__val-card--disabled',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className='form-builder-props__val-icon'>
                <Icon name={ROW_ICON[key] ?? 'settings'} size={16} />
              </span>
              <span className='form-builder-props__val-text'>
                <span className='form-builder-props__val-title'>
                  {t(labelKey)}
                  {INFO_ROWS.has(key) && (
                    <span className='form-builder-props__val-info' title={t(`${labelKey}_desc`)}>
                      <Icon name='info' size={13} />
                    </span>
                  )}
                </span>
                {!INFO_ROWS.has(key) && (
                  <span className='form-builder-props__val-desc'>{t(`${labelKey}_desc`)}</span>
                )}
              </span>
              <Switch
                checked={vp[key] === true}
                disabled={rowDisabled}
                aria-label={t(labelKey)}
                // Name the culprit — a switch that silently goes dead reads as a
                // bug. Composed from the blocker's own row label, or the
                // geofence gate's reason.
                title={
                  blocked.length > 0
                    ? t('forms_builder_props_val_blocked_by', {
                        blocker: t(`forms_builder_props_val_${blocked[0]}`),
                      })
                    : (geofenceBlockReason ?? undefined)
                }
                onChange={(e) => toggleRow(key, e.target.checked)}
                size='sm'
              />
            </div>

            {vp[key] === true && dependents.length > 0 && (
              <div className='form-builder-props__val-sub'>
                {key === 'geofence' ? geofenceCluster() : dependents.map(subControl)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FormBuilderValidationSection;
